import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Autocomplete
} from '@mui/material';
import { useQuery } from 'react-query';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import React, { FC, useState, useMemo, useEffect } from 'react';
import {
  getCompactionLetter,
  getCompactionLetterInfo,
  getSoilConditions,
  getSewerPumpTypes,
  getSurveyRequirements,
  getEnvironmentalConcerns,
  putJobLotInfo
} from '../../../../fetch';
import { IDropdownResponse, IJobLotInfo } from '../../../../models';
import { Loader, SelectAsync } from '../../../../components';
import { LotInfoImages } from './lot-info-images';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDBFriendlyDateTime } from '../../../../helpers/format';

const LotSchema = Yup.object().shape({
  compactionTestExpiration: Yup.string(),
  possibleLotFitIssue: Yup.boolean(),
  selectedSurveyRequirements: Yup.array().of(Yup.string()),
  selectedEnvironmentalConcerns: Yup.array().of(Yup.string()),
  lotFitNotes: Yup.string(),
  setbackFront: Yup.number(),
  setbackShortSide: Yup.number(),
  setbackLongSide: Yup.number(),
  setbackYardStreetSide: Yup.number(),
  setbackRear: Yup.number(),
  depthOfUndercut: Yup.number(),
  hasPostTensionedSlab: Yup.string(),
  lotFencedStandard: Yup.string(),
  minimumFFE: Yup.number(),
  hasMinimumFFE: Yup.string(),
  hasSewerPump: Yup.string(),
  sewerPumpType: Yup.string(),
  needsFoundationInspection: Yup.string(),
  notes: Yup.string()
});

interface ILotInformation {
  lotInfo: IJobLotInfo;
  isLoadingLotInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchLotInfo: () => void;
}

export const LotInformation: FC<ILotInformation> = ({
  lotInfo,
  isLoadingLotInfo,
  setDirtyForm,
  refetchLotInfo
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();
  const history = useHistory();
  const [selectedSurveyRequirementValues, setSelectedSurveyRequirementValues] = useState<
    IDropdownResponse[]
  >([]);
  const [inputSelectedSurveyRequirement, setInputSelectedSurveyRequirement] = useState('');
  const [selectedEnvironmentalConcernValues, setSelectedEnvironmentalConcernValues] = useState<
    IDropdownResponse[]
  >([]);
  const [inputSelectedEnvironmentalConcernValue, setInputSelectedEnvironmentalConcernValue] =
    useState('');
  const [letterOne, setLetterOne] = useState<any>(null);
  const [letterTwo, setLetterTwo] = useState<any>(null);

  const { isLoading: isLoadingSurveyRequirements, data: surveyRequirements } = useQuery<
    IDropdownResponse[],
    Error
  >(['selected-survey-requirements'], () => getSurveyRequirements(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return lotInfo?.surveyRequirements?.includes(record.value);
      });
      setSelectedSurveyRequirementValues(options);
    }
  });
  const surveyRequirementsData = useMemo(() => surveyRequirements ?? [], [surveyRequirements]);

  const { isLoading: isLoadingEnvironmentalConcerns, data: environmentalConcerns } = useQuery<
    IDropdownResponse[],
    Error
  >(['selected-environmental-concerns'], () => getEnvironmentalConcerns(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return lotInfo?.environmentalConcerns?.includes(record.value);
      });
      setSelectedEnvironmentalConcernValues(options);
    }
  });
  const environmentalConcernsData = useMemo(
    () => environmentalConcerns ?? [],
    [environmentalConcerns]
  );

  const { data: compactionTestLetterInfo, refetch: refetchCompactionTestLetterInfo } = useQuery<
    any,
    Error
  >(['getCompactionLetterInfo'], () => getCompactionLetterInfo(jobId));

  const generateFileDownload = async () => {
    try {
      if (compactionTestLetterInfo?.hasCompactionTestLetterOne === true) {
        const letterOne = await getCompactionLetter(jobId, 'CompactionTestLetterOne');
        setLetterOne(letterOne);
      }

      if (compactionTestLetterInfo?.hasCompactionTestLetterTwo === true) {
        const letterTwo = await getCompactionLetter(jobId, 'CompactionTestLetterTwo');
        setLetterTwo(letterTwo);
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error generating compaction letters', {
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    generateFileDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compactionTestLetterInfo]);

  return (
    <>
      {isLoadingLotInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingLotInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            compactionTestExpiration: lotInfo?.compactionTestExpiration
              ? new Date(lotInfo?.compactionTestExpiration)
              : undefined,
            possibleLotFitIssues:
              lotInfo?.possibleLotFitIssues === true
                ? 'Yes'
                : lotInfo?.possibleLotFitIssues === false
                ? 'No'
                : '',
            lotFitNotes: lotInfo?.lotFitNotes ?? '',
            setbackFront: lotInfo?.setbackFront ?? '',
            setbackShortSide: lotInfo?.setbackShortSide ?? '',
            setbackLongSide: lotInfo?.setbackLongSide ?? '',
            setbackYardStreetSide: lotInfo?.setbackYardStreetSide ?? '',
            setbackRear: lotInfo?.setbackRear ?? '',
            soilConditions: lotInfo?.soilConditions ?? '',
            depthOfUndercut: lotInfo?.depthOfUndercut ?? '',
            hasPostTensionedSlab:
              lotInfo?.hasPostTensionedSlab === true
                ? 'Yes'
                : lotInfo?.hasPostTensionedSlab === false
                ? 'No'
                : '',
            surveyRequirements: lotInfo?.surveyRequirements ?? [],
            lotFencedStandard:
              lotInfo?.lotFencedStandard === true
                ? 'Yes'
                : lotInfo?.lotFencedStandard === false
                ? 'No'
                : '',
            environmentalConcerns: lotInfo?.environmentalConcerns ?? [],
            minimumFFE: lotInfo?.minimumFFE ?? '',
            hasMinimumFFE:
              lotInfo?.hasMinimumFFE === true
                ? 'Yes'
                : lotInfo?.hasMinimumFFE === false
                ? 'No'
                : '',
            hasSewerPump:
              lotInfo?.hasSewerPump === true ? 'Yes' : lotInfo?.hasSewerPump === false ? 'No' : '',
            sewerPumpType: lotInfo?.sewerPumpType ?? '',
            needsFoundationInspection:
              lotInfo?.needsFoundationInspection === true
                ? 'Yes'
                : lotInfo?.needsFoundationInspection === false
                ? 'No'
                : '',
            notes: lotInfo?.notes ?? '',
            compactionTestLetterOne: compactionTestLetterInfo?.hasCompactionTestLetterOne ?? '',
            compactionTestLetterTwo: compactionTestLetterInfo?.hasCompactionTestLetterTwo ?? ''
          }}
          validationSchema={LotSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IJobLotInfo = {
                ...values,
                compactionTestExpiration: formatDBFriendlyDateTime(values.compactionTestExpiration),
                possibleLotFitIssues: values.possibleLotFitIssues === 'Yes' ? true : false,
                hasPostTensionedSlab: values.hasPostTensionedSlab === 'Yes' ? true : false,
                lotFencedStandard: values.lotFencedStandard === 'Yes' ? true : false,
                hasMinimumFFE: values.hasMinimumFFE === 'Yes' ? true : false,
                hasSewerPump: values.hasSewerPump === 'Yes' ? true : false,
                needsFoundationInspection:
                  values.needsFoundationInspection === 'Yes' ? true : false,
                setbackFront: values.setbackFront ? Number(values.setbackFront) : null,
                setbackShortSide: values.setbackShortSide ? Number(values.setbackShortSide) : null,
                setbackLongSide: values.setbackLongSide ? Number(values.setbackLongSide) : null,
                setbackYardStreetSide: values.setbackYardStreetSide
                  ? Number(values.setbackYardStreetSide)
                  : null,
                setbackRear: values.setbackRear ? Number(values.setbackRear) : null,
                depthOfUndercut: values.depthOfUndercut ? Number(values.depthOfUndercut) : null,
                minimumFFE: values.minimumFFE ? Number(values.minimumFFE) : null,
                soilCondition: values.soilConditions ?? ''
              };

              await putJobLotInfo(jobId, data);
              refetchLotInfo();
              enqueueSnackbar('Lot Info has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Lot Info was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }
            return (
              <Form onSubmit={handleSubmit}>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <LotInfoImages
                        jobId={jobId}
                        refetchLetter={refetchCompactionTestLetterInfo}
                        photoOwnerType={'CompactionTestLetterOne'}
                        imageTitle={'Compaction Test Letter'}
                        image={letterOne}
                      ></LotInfoImages>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LotInfoImages
                        jobId={jobId}
                        refetchLetter={refetchCompactionTestLetterInfo}
                        photoOwnerType={'CompactionTestLetterTwo'}
                        imageTitle={'Compaction Test Letter Two'}
                        image={letterTwo}
                      ></LotInfoImages>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={1} mt={1}>
                        <Grid item xs={12} sm={6}>
                          <DatePicker
                            label="Compaction Letter Expiration Date"
                            onChange={(date: any) => {
                              setFieldValue('compactionTestExpiration', date);
                            }}
                            value={values?.compactionTestExpiration}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                value: values.compactionTestExpiration
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl component="fieldset">
                            <FormLabel>Possible lot fit issues?</FormLabel>
                            <RadioGroup
                              row
                              aria-label="possibleLotFitIssues"
                              name="possibleLotFitIssues"
                              value={values?.possibleLotFitIssues}
                              onChange={(e, value) =>
                                setFieldValue('possibleLotFitIssues', e.target.value)
                              }
                            >
                              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={1}>
                        {values?.possibleLotFitIssues === 'Yes' && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={6}
                              variant="outlined"
                              autoComplete="nope"
                              label="Lot Fit Notes"
                              name="lotFitNotes"
                              size="small"
                              value={values.lotFitNotes}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('lotFitNotes', e.target.value)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="nope"
                        label="Setback Front"
                        name="setbackFront"
                        size="small"
                        value={values.setbackFront}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('setbackFront', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="nope"
                        label="Setback Shot Side"
                        name="setbackShortSide"
                        size="small"
                        value={values.setbackShortSide}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('setbackShortSide', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="nope"
                        label="Setback Long Side"
                        name="setbackLongSide"
                        size="small"
                        value={values.setbackLongSide}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('setbackLongSide', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="nope"
                        label="Setback Side Yard on Street"
                        name="setbackYardStreetSide"
                        size="small"
                        value={values.setbackYardStreetSide}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('setbackYardStreetSide', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="nope"
                        label="Setback Rear"
                        name="setbackRear"
                        size="small"
                        value={values.setbackRear}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('setbackRear', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectAsync
                        label="Soil Conditions"
                        name="soilConditions"
                        onClear={() => {
                          setFieldValue('soilConditions', '');
                        }}
                        value={values.soilConditions}
                        hasClear
                        apiRequest={() => getSoilConditions()}
                        transformResponse={(res: IDropdownResponse[]) =>
                          res.map(r => ({
                            label: r.description,
                            value: r.value
                          }))
                        }
                      />
                    </Grid>
                    {values.soilConditions === 'WeakNonexpansive' && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          variant="outlined"
                          autoComplete="nope"
                          label="Depth of Undercut"
                          name="depthOfUndercut"
                          size="small"
                          value={values.depthOfUndercut}
                          onBlur={handleBlur}
                          onChange={e => setFieldValue('depthOfUndercut', e.target.value)}
                        />
                      </Grid>
                    )}
                    {values.soilConditions === 'WeakExpansive' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl component="fieldset">
                            <FormLabel>Post Tensioned Slab</FormLabel>
                            <RadioGroup
                              row
                              aria-label="hasPostTensionedSlab"
                              name="hasPostTensionedSlab"
                              value={values?.hasPostTensionedSlab}
                              onChange={(e, value) =>
                                setFieldValue('hasPostTensionedSlab', e.target.value)
                              }
                            >
                              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            autoComplete="nope"
                            label="Depth of Undercut"
                            name="depthOfUndercut"
                            size="small"
                            value={values.depthOfUndercut}
                            onBlur={handleBlur}
                            onChange={e => setFieldValue('depthOfUndercut', e.target.value)}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        multiple
                        id="surveyRequirements"
                        value={selectedSurveyRequirementValues}
                        onChange={(event, newValue: any[]) => {
                          setSelectedSurveyRequirementValues(newValue);
                          setFieldValue(
                            'surveyRequirements',
                            newValue.map(val => val.value)
                          );
                        }}
                        inputValue={inputSelectedSurveyRequirement}
                        onInputChange={(event, newInputValue) => {
                          setInputSelectedSurveyRequirement(newInputValue);
                        }}
                        options={surveyRequirementsData}
                        getOptionLabel={option => option.description}
                        filterSelectedOptions
                        renderOption={(props, option: any) => {
                          return (
                            <li {...props} key={`${option.value}`}>
                              {option.description}
                            </li>
                          );
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            disabled={isLoadingSurveyRequirements}
                            label={
                              isLoadingSurveyRequirements ? 'Loading...' : 'Survey Requirements'
                            }
                            placeholder="Selected Survey Requirements"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        multiple
                        id="environmentalConcerns"
                        value={selectedEnvironmentalConcernValues}
                        onChange={(event, newValue: IDropdownResponse[]) => {
                          setSelectedEnvironmentalConcernValues(newValue);
                          setFieldValue(
                            'environmentalConcerns',
                            newValue.map(val => val.value)
                          );
                        }}
                        inputValue={inputSelectedEnvironmentalConcernValue}
                        onInputChange={(event, newInputValue) => {
                          setInputSelectedEnvironmentalConcernValue(newInputValue);
                        }}
                        options={environmentalConcernsData}
                        getOptionLabel={option => option.description}
                        filterSelectedOptions
                        renderOption={(props, option: IDropdownResponse) => {
                          return (
                            <li {...props} key={`${option.value}`}>
                              {option.description}
                            </li>
                          );
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            disabled={isLoadingEnvironmentalConcerns}
                            label={
                              isLoadingEnvironmentalConcerns
                                ? 'Loading...'
                                : 'Environmental Concerns'
                            }
                            placeholder="Selected Environmental Concerns"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel>Lot Fenced Standard</FormLabel>
                        <RadioGroup
                          row
                          aria-label="lotFencedStandard"
                          name="lotFencedStandard"
                          value={values?.lotFencedStandard}
                          onChange={(e, value) =>
                            setFieldValue('lotFencedStandard', e.target.value)
                          }
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel>Needs Foundation Inspection</FormLabel>
                        <RadioGroup
                          row
                          aria-label="needsFoundationInspection"
                          name="needsFoundationInspection"
                          value={values?.needsFoundationInspection}
                          onChange={(e, value) =>
                            setFieldValue('needsFoundationInspection', e.target.value)
                          }
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel>Is There a Minimum FFE</FormLabel>
                        <RadioGroup
                          row
                          aria-label="hasMinimumFFE"
                          name="hasMinimumFFE"
                          value={values?.hasMinimumFFE}
                          onChange={(e, value) => setFieldValue('hasMinimumFFE', e.target.value)}
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel>Sewer Pump</FormLabel>
                        <RadioGroup
                          row
                          aria-label="hasSewerPump"
                          name="hasSewerPump"
                          value={values?.hasSewerPump}
                          onChange={(e, value) => setFieldValue('hasSewerPump', e.target.value)}
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {values?.hasMinimumFFE === 'Yes' && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          variant="outlined"
                          autoComplete="nope"
                          label="Minimum FFE"
                          name="minimumFFE"
                          size="small"
                          value={values.minimumFFE}
                          onBlur={handleBlur}
                          onChange={e => setFieldValue('minimumFFE', e.target.value)}
                        />
                      </Grid>
                    )}
                    {values.hasSewerPump === 'Yes' && (
                      <Grid item xs={12} sm={6}>
                        <SelectAsync
                          label="Sewer Pump Type"
                          name="sewerPumpType"
                          onClear={() => {
                            setFieldValue('sewerPumpType', '');
                          }}
                          hasClear
                          apiRequest={() => getSewerPumpTypes()}
                          transformResponse={(res: IDropdownResponse[]) =>
                            res.map(r => ({
                              label: r.description,
                              value: r.value
                            }))
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        variant="outlined"
                        autoComplete="nope"
                        label="Notes"
                        name="notes"
                        size="small"
                        value={values.notes}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('notes', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box mt={1} mb={0.5}>
                  <Divider />
                </Box>
                <ButtonGroup>
                  <Button
                    disabled={!dirty || isSubmitting || !isValid}
                    type="submit"
                    startIcon={jobId ? <Save /> : <Add />}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="inherit"
                    startIcon={<Close />}
                    onClick={() => {
                      history.push('/development/jobs');
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
